// ABOUTME: Loads several workflow definitions by code from the workflow REST API, keyed by code.
// ABOUTME: A code whose definition cannot be loaded is left out; `loading` stays true until all have settled.

import { useEffect, useMemo, useState } from 'react';

import { getWorkflow, WorkflowDefinition } from '../api/workflowApi';

export type WorkflowsByCode = Record<string, WorkflowDefinition>;

export function useWorkflowDefinitions(workflowCodes: string[]) {
    // Keyed on the codes' content, not on the array identity callers rebuild on every render.
    const codesKey = useMemo(() => [...new Set(workflowCodes)].sort().join('|'), [workflowCodes]);
    const [workflows, setWorkflows] = useState<WorkflowsByCode>({});
    const [loading, setLoading] = useState(!!codesKey);

    useEffect(() => {
        if (!codesKey) {
            setWorkflows({});
            setLoading(false);
            return undefined;
        }
        let cancelled = false;
        setLoading(true);
        const codes = codesKey.split('|');
        Promise.allSettled(codes.map(code => getWorkflow(code)))
            .then(results => {
                if (cancelled) return;
                const loaded: WorkflowsByCode = {};
                results.forEach((result, index) => {
                    if (result.status === 'fulfilled') loaded[codes[index]] = result.value;
                });
                setWorkflows(loaded);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [codesKey]);

    return { workflows, loading };
}
